import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const services = [
    { name: 'Bladder Cancer Urology', path: '/services/bladder-cancer' },
    { name: 'Bladder Stones Urology', path: '/services/bladder-stones' },
    { name: 'Enlarged Prostate (BPH)', path: '/services/bph' },
    { name: 'Erectile Dysfunction', path: '/services/erectile-dysfunction' },
    { name: 'Fatigue Urology', path: '/services/fatigue' },
    { name: 'Fecal Incontinence Urology', path: '/services/fecal-incontinence' },
    { name: 'GAINSWave Therapy', path: '/services/gainswave' },
    { name: 'Hypogonadism Urology', path: '/services/hypogonadism' },
    { name: 'InterStim Urology', path: '/services/interstim' },
    { name: 'Kidney Stones Urology', path: '/services/kidney-stones' },
    { name: 'Low Libido Urology', path: '/services/low-libido' },
    { name: 'Low Testosterone', path: '/services/low-testosterone' },
    { name: 'OAB Urology', path: '/services/oab' },
    { name: "Peyronie's Disease", path: '/services/peyronies-disease' },
    { name: 'Testopel Urology', path: '/services/testopel' },
    { name: 'Testosterone Urology', path: '/services/testosterone' },
    { name: 'Testosterone Pellets Urology', path: '/services/testosterone-pellets' },
    { name: 'Urge Incontinence Urology', path: '/services/urge-incontinence' },
    { name: 'Urinary Frequency Urology', path: '/services/urinary-frequency' },
    { name: 'Urinary Retention Urology', path: '/services/urinary-retention' },
    { name: 'Urinary Urgency Urology', path: '/services/urinary-urgency' },
    { name: 'Urodynamics', path: '/services/urodynamics' },
    { name: 'UroLift', path: '/services/urolift' },
    { name: 'Vasectomy', path: '/services/vasectomy' },
  ];

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'New Patients', path: '/new-patients' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="header-logo"
          >
            <span className="font-heading text-xl md:text-2xl font-bold text-primary tracking-tight">
              HAVKIN <span className="font-normal">UROLOGY</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/') ? 'text-brand bg-brand/10' : 'text-slate-600 hover:text-brand hover:bg-slate-50'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.includes('/mens-health') ? 'text-brand bg-brand/10' : 'text-slate-600 hover:text-brand hover:bg-slate-50'
                  }`}
                  data-testid="nav-mens-health-dropdown"
                >
                  Men's Health
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[28rem] max-h-[80vh] overflow-y-auto p-2">
                <DropdownMenuItem asChild>
                  <Link to="/mens-health" className="w-full font-semibold" data-testid="nav-mens-health-hub">
                    Men's Health Hub
                  </Link>
                </DropdownMenuItem>
                <div className="grid grid-cols-2 gap-0">
                  {services.map((service) => (
                    <DropdownMenuItem key={service.path} asChild>
                      <Link to={service.path} className="w-full" data-testid={`nav-${service.path.split('/').pop()}`}>
                        {service.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-brand bg-brand/10' : 'text-slate-600 hover:text-brand hover:bg-slate-50'
                }`}
                data-testid={`nav-${link.path.substring(1)}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center space-x-3">
            <a 
              href="tel:+13215004545"
              className="flex items-center px-4 py-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors"
              data-testid="header-call-btn"
              onClick={() => {
                if (typeof window !== 'undefined' && window.gtag) {
                  window.gtag('event', 'call_click', { event_category: 'engagement' });
                }
              }}
            >
              <Phone className="h-4 w-4 mr-2" />
              (321) 500-4545
            </a>
            <Button 
              asChild 
              className="bg-brand hover:bg-brand-hover text-white rounded-full px-6"
              data-testid="header-appointment-btn"
            >
              <Link to="/contact">Request Appointment</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/"
              className="block px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-home"
            >
              Home
            </Link>
            <Link
              to="/mens-health"
              className="block px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-mens-health"
            >
              Men's Health Hub
            </Link>
            <div className="pl-4 space-y-1">
              {services.map((service) => (
                <Link
                  key={service.path}
                  to={service.path}
                  className="block px-4 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-50"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${service.path.split('/').pop()}`}
                >
                  {service.name}
                </Link>
              ))}
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.path.substring(1)}`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <a
                href="tel:+13215004545"
                className="flex items-center justify-center w-full px-4 py-3 rounded-full border border-brand text-brand font-medium"
                data-testid="mobile-call-btn"
              >
                <Phone className="h-4 w-4 mr-2" />
                (321) 500-4545
              </a>
              <Button 
                asChild 
                className="w-full bg-brand hover:bg-brand-hover text-white rounded-full"
                data-testid="mobile-appointment-btn"
              >
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  Request Appointment
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
